import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import AuthContext from './AuthContext';
import {
  applyClockCorrection,
  DEFAULT_TIMEZONE_SETTINGS,
  getZonedParts
} from '../utils/appTimezone';
import {
  resolveIanaTimezone,
  shouldUseBusinessFixedOffset
} from '../utils/countryTimezone';

const TimezoneContext = createContext(null);

const pad = (value) => String(value).padStart(2, '0');

const toValidDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatWithFixedOffset = (date, offsetMinutes) => {
  const parts = getZonedParts(date, offsetMinutes);
  if (!parts) return null;
  return `${pad(parts.day)}/${pad(parts.month)}/${parts.year} ${pad(parts.hour)}:${pad(parts.minute)}`;
};

const formatWithIntl = (date, timeZone, locale) => {
  try {
    const formatter = new Intl.DateTimeFormat(locale, {
      timeZone,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    const parts = formatter.formatToParts(date);
    const pick = (type) => parts.find((part) => part.type === type)?.value || '';
    return `${pick('day')}/${pick('month')}/${pick('year')} ${pick('hour')}:${pick('minute')}`;
  } catch (_) {
    return null;
  }
};

export const TimezoneProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [timezoneSettings, setTimezoneSettings] = useState({ ...DEFAULT_TIMEZONE_SETTINGS });
  const [preview, setPreview] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const fetchTimezoneSettings = useCallback(async () => {
    try {
      const res = await axios.get('/api/timezone-settings');
      setTimezoneSettings({
        ...DEFAULT_TIMEZONE_SETTINGS,
        ...(res.data?.timezoneSettings || {})
      });
      setPreview(res.data?.preview || null);
    } catch (_) {
      setTimezoneSettings({ ...DEFAULT_TIMEZONE_SETTINGS });
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchTimezoneSettings();
  }, [fetchTimezoneSettings]);

  useEffect(() => {
    const onSettingsUpdated = () => {
      fetchTimezoneSettings();
    };
    window.addEventListener('settingsUpdated', onSettingsUpdated);
    return () => window.removeEventListener('settingsUpdated', onSettingsUpdated);
  }, [fetchTimezoneSettings]);

  const userDisplayContext = useMemo(() => ({
    country: user?.country || user?.lastLoginLocation?.country || '',
    countryCode: user?.lastLoginLocation?.countryCode || '',
    timezone: user?.timezone || ''
  }), [user]);

  const formatDateTime = useCallback((value, fallback = '—', options = {}) => {
    const date = toValidDate(value);
    if (!date) return fallback;

    const settings = { ...timezoneSettings, ...(options.timezoneSettings || {}) };
    const corrected = options.skipClockCorrection
      ? date
      : applyClockCorrection(date, settings.clockCorrectionMinutes);
    const locale = options.language === 'ar' ? 'ar-EG' : 'en-GB';
    const viewer = { ...userDisplayContext, ...(options.viewer || {}) };

    const useBusinessOffset = options.mode === 'business'
      || shouldUseBusinessFixedOffset({
        ...viewer,
        businessCountryCode: settings.businessCountryCode
      });

    if (useBusinessOffset) {
      return formatWithFixedOffset(corrected, settings.businessUtcOffsetMinutes) || fallback;
    }

    const iana = resolveIanaTimezone({
      ...viewer,
      businessCountryCode: settings.businessCountryCode
    });
    return formatWithIntl(corrected, iana, locale)
      || formatWithFixedOffset(corrected, settings.businessUtcOffsetMinutes)
      || fallback;
  }, [timezoneSettings, userDisplayContext]);

  const formatBusinessDateTime = useCallback((value, fallback = '—') => {
    return formatDateTime(value, fallback, { mode: 'business' });
  }, [formatDateTime]);

  const value = useMemo(() => ({
    timezoneSettings,
    preview,
    loaded,
    refreshTimezoneSettings: fetchTimezoneSettings,
    formatDateTime,
    formatBusinessDateTime
  }), [
    timezoneSettings,
    preview,
    loaded,
    fetchTimezoneSettings,
    formatDateTime,
    formatBusinessDateTime
  ]);

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
};

export const useTimezone = () => {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezone must be used within TimezoneProvider');
  }
  return context;
};

export default TimezoneContext;
